import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCheck, CheckCircle2, Download, FileUp, Loader2, PenLine, RotateCw, UploadCloud } from 'lucide-react';
import type {
  DocumentAcceptanceRow,
  DocumentAcceptanceStatus,
  DocumentCountersignRow,
  DocumentCountersignStatus,
  DocumentPublicationDetail,
  SignedRevisionPreview,
} from '@ip/shared-types';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useDocumentsApi } from '@/services/api';
import { sanitizeFileNamePart } from '@/utils/download';
import { SignatureDetails } from '@/components/documents/SignatureBadges';
import { t } from '@/i18n';

const STATUS_CHIP: Record<DocumentAcceptanceStatus, string> = {
  pending: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  returned: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  accepted: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  signed: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

// Representative countersigns (umowa spec 2026-07-23 §8c): 'waiting' = not
// its turn yet (employee or an earlier representative must sign first).
const COUNTERSIGN_CHIP: Record<DocumentCountersignStatus, string> = {
  waiting: 'text-muted-foreground',
  pending: 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  signed: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
};

interface PublicationDetailSheetProps {
  nip: string;
  publicationId: string | null;
  onOpenChange: (open: boolean) => void;
  /** Called after archive or mark-signed so the caller can refresh the list. */
  onChanged: () => void;
}

/**
 * Publication detail sheet (Task 6, spec §8.4): full acceptance progress —
 * per-employee status/method/date, PDF download, and (physical mode only) a
 * signed-scan upload per still-pending row.
 */
export function PublicationDetailSheet({ nip, publicationId, onOpenChange, onChanged }: PublicationDetailSheetProps) {
  const {
    getPublication, downloadPublication, markSigned, attachSignedRevision, previewSignedRevision,
    confirmReturn, downloadAcceptanceReturned, verifyAcceptanceSignature, countersignSign,
    countersignVerifySignature, countersignFileDownload,
  } = useDocumentsApi();

  const [detail, setDetail] = useState<DocumentPublicationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Digital-signature check of returned scans: auto-run once per eligible row.
  const [verifyingRows, setVerifyingRows] = useState<Record<string, boolean>>({});
  const [verifyErrorRows, setVerifyErrorRows] = useState<Record<string, boolean>>({});
  const attemptedRef = useRef<Set<string>>(new Set());

  const [signTarget, setSignTarget] = useState<DocumentAcceptanceRow | null>(null);
  const [signFile, setSignFile] = useState<File | null>(null);
  const [signing, setSigning] = useState(false);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);

  // A NEWER signed revision of an already completed row (2026-09-05): the PDF
  // countersigned by a representative AFTER the employee's signature settled
  // the acceptance. Server-side gated to a provable revision of the same file.
  const [revisionTarget, setRevisionTarget] = useState<DocumentAcceptanceRow | null>(null);
  const [revisionFile, setRevisionFile] = useState<File | null>(null);
  const [attachingRevision, setAttachingRevision] = useState(false);
  // Two steps, deliberately: the server's dry run first shows WHICH signatures
  // the file adds (name, kind, issuer) — the operator applies a named signer,
  // never a blind upload. Reset whenever the file changes.
  const [revisionPreview, setRevisionPreview] = useState<SignedRevisionPreview | null>(null);
  const [previewingRevision, setPreviewingRevision] = useState(false);

  // Representative countersigns (umowa spec 2026-07-23 §8c).
  const [csSignTarget, setCsSignTarget] = useState<DocumentCountersignRow | null>(null);
  const [csSignFile, setCsSignFile] = useState<File | null>(null);
  const [csSigning, setCsSigning] = useState(false);
  const [csVerifyingRows, setCsVerifyingRows] = useState<Record<string, boolean>>({});
  const [csVerifyErrorRows, setCsVerifyErrorRows] = useState<Record<string, boolean>>({});
  const csAttemptedRef = useRef<Set<string>>(new Set());
  const [csDownloadingId, setCsDownloadingId] = useState<string | null>(null);

  const open = publicationId !== null;

  // Signing a countersign advances the NEXT row server-side ('waiting' →
  // 'pending'), so mutations reload the whole detail instead of patching rows.
  const loadDetail = useCallback(async () => {
    if (!publicationId) return;
    setLoading(true);
    try {
      setDetail(await getPublication(nip, publicationId));
    } catch (error) {
      toast.error(t('documents.publications.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [nip, publicationId, getPublication]);

  useEffect(() => {
    // New publication (or closed sheet): forget prior verify attempts.
    attemptedRef.current.clear();
    csAttemptedRef.current.clear();
    setVerifyingRows({});
    setVerifyErrorRows({});
    setCsVerifyingRows({});
    setCsVerifyErrorRows({});
    if (!publicationId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPublication(nip, publicationId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((error) => {
        if (!cancelled) {
          toast.error(t('documents.publications.loadError'));
          console.error(error);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [nip, publicationId, getPublication]);

  const runVerify = useCallback(async (row: DocumentAcceptanceRow) => {
    if (!publicationId) return;
    attemptedRef.current.add(row.id);
    setVerifyErrorRows((prev) => ({ ...prev, [row.id]: false }));
    setVerifyingRows((prev) => ({ ...prev, [row.id]: true }));
    try {
      const result = await verifyAcceptanceSignature(nip, publicationId, row.id);
      setDetail((prev) =>
        prev
          ? { ...prev, acceptances: prev.acceptances.map((a) => (a.id === row.id ? { ...a, signatureVerification: result } : a)) }
          : prev,
      );
      // Verification can settle the paperwork server-side (a scan already
      // signed by the employee / a representative), so pick up the new
      // statuses instead of showing a stale "Zwrócono"/"Oczekuje".
      await loadDetail();
    } catch (error) {
      setVerifyErrorRows((prev) => ({ ...prev, [row.id]: true }));
      console.error(error);
    } finally {
      setVerifyingRows((prev) => ({ ...prev, [row.id]: false }));
    }
  }, [nip, publicationId, verifyAcceptanceSignature, loadDetail]);

  // Auto-trigger verification ONCE per returned/signed row that carries a scan
  // and has no persisted result yet; errors surface a manual retry, no re-fire.
  useEffect(() => {
    if (!detail) return;
    for (const row of detail.acceptances) {
      const eligible =
        row.hasSignedFile ?? (row.status === 'returned' || row.status === 'signed');
      if (eligible && row.signatureVerification == null && !attemptedRef.current.has(row.id)) {
        runVerify(row);
      }
    }
  }, [detail, runVerify]);

  const handleDownload = async () => {
    if (!publicationId) return;
    try {
      setDownloading(true);
      await downloadPublication(
        nip,
        publicationId,
        detail ? `${sanitizeFileNamePart(detail.number)}.pdf` : `dokument-${publicationId}.pdf`,
      );
    } catch {
      /* session expired — handled by the global modal */
    } finally {
      setDownloading(false);
    }
  };

  const handleMarkSigned = async () => {
    if (!publicationId || !signTarget || !signFile) return;
    const fd = new FormData();
    fd.append('file', signFile);
    try {
      setSigning(true);
      const updated = await markSigned(nip, publicationId, signTarget.id, fd);
      setDetail((prev) =>
        prev
          ? { ...prev, acceptances: prev.acceptances.map((a) => (a.id === updated.id ? updated : a)) }
          : prev,
      );
      // Employee acceptance reaching a final state activates the first
      // countersign server-side ('waiting' → 'pending') — reload the whole
      // detail so the representative's sign action shows up immediately.
      if (detail?.countersigns?.length) await loadDetail();
      toast.success(t('documents.publications.markSignedSuccess'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('documents.publications.markSignedError'));
      console.error(error);
    } finally {
      setSigning(false);
      setSignTarget(null);
      setSignFile(null);
    }
  };

  const handlePreviewRevision = async () => {
    if (!publicationId || !revisionTarget || !revisionFile) return;
    const fd = new FormData();
    fd.append('file', revisionFile);
    try {
      setPreviewingRevision(true);
      setRevisionPreview(await previewSignedRevision(nip, publicationId, revisionTarget.id, fd));
    } catch (error) {
      // 409 (duplicate) / 422 (refused: reason) — the server's message names it.
      toast.error(error instanceof Error ? error.message : t('documents.publications.attachRevisionPreviewError'));
      console.error(error);
    } finally {
      setPreviewingRevision(false);
    }
  };

  const handleAttachRevision = async () => {
    if (!publicationId || !revisionTarget || !revisionFile || !revisionPreview) return;
    const fd = new FormData();
    fd.append('file', revisionFile);
    try {
      setAttachingRevision(true);
      await attachSignedRevision(nip, publicationId, revisionTarget.id, fd);
      toast.success(t('documents.publications.attachRevisionSuccess'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('documents.publications.attachRevisionError'));
      console.error(error);
    } finally {
      // Reload on BOTH outcomes: the revision's signatures may have just signed
      // representative rows (countersign automation), and a 409 "already the
      // stored file" re-runs that automation server-side too.
      await loadDetail();
      setAttachingRevision(false);
      setRevisionTarget(null);
      setRevisionFile(null);
      setRevisionPreview(null);
    }
  };

  // Save the scan routed back for a row so HR can READ it before confirming.
  // No stored filename — the extension is inferred from the response type.
  const handleDownloadReturned = async (row: DocumentAcceptanceRow) => {
    if (!publicationId) return;
    setDownloadingRowId(row.id);
    try {
      const base = detail ? sanitizeFileNamePart(detail.number) : publicationId;
      await downloadAcceptanceReturned(
        nip,
        publicationId,
        row.id,
        `${base}-${sanitizeFileNamePart(row.employeeName)}-podpisany`,
      );
    } catch {
      /* session expired — handled by the global modal */
    } finally {
      setDownloadingRowId(null);
    }
  };

  // HR confirmation of a returned signed copy (documents plan 4): `returned` -> `signed`.
  const handleConfirmReturn = async (row: DocumentAcceptanceRow) => {
    if (!publicationId) return;
    setConfirmingId(row.id);
    try {
      const updated = await confirmReturn(nip, publicationId, row.id);
      setDetail((prev) =>
        prev
          ? { ...prev, acceptances: prev.acceptances.map((a) => (a.id === updated.id ? updated : a)) }
          : prev,
      );
      // Same as mark-signed: the countersign chain may have just activated.
      if (detail?.countersigns?.length) await loadDetail();
      toast.success(t('documents.publications.confirmReturnSuccess'));
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('documents.publications.confirmReturnError'));
      console.error(error);
    } finally {
      setConfirmingId(null);
    }
  };

  // ---- Representative countersigns (umowa spec 2026-07-23 §8c) ----

  const runCsVerify = useCallback(async (row: DocumentCountersignRow) => {
    if (!publicationId) return;
    csAttemptedRef.current.add(row.id);
    setCsVerifyErrorRows((prev) => ({ ...prev, [row.id]: false }));
    setCsVerifyingRows((prev) => ({ ...prev, [row.id]: true }));
    try {
      const result = await countersignVerifySignature(nip, publicationId, row.id);
      setDetail((prev) =>
        prev
          ? {
              ...prev,
              countersigns: prev.countersigns?.map((c) =>
                c.id === row.id ? { ...c, signatureVerification: result } : c,
              ),
            }
          : prev,
      );
    } catch (error) {
      setCsVerifyErrorRows((prev) => ({ ...prev, [row.id]: true }));
      console.error(error);
    } finally {
      setCsVerifyingRows((prev) => ({ ...prev, [row.id]: false }));
    }
  }, [nip, publicationId, countersignVerifySignature]);

  // Auto-verify ONCE per signed countersign that carries a file and has no
  // persisted result yet — mirrors the acceptance-scan behavior above.
  useEffect(() => {
    if (!detail?.countersigns) return;
    for (const row of detail.countersigns) {
      if (
        row.status === 'signed' &&
        row.hasFile &&
        row.signatureVerification == null &&
        !csAttemptedRef.current.has(row.id)
      ) {
        runCsVerify(row);
      }
    }
  }, [detail, runCsVerify]);

  const handleCountersignSign = async () => {
    if (!publicationId || !csSignTarget) return;
    try {
      setCsSigning(true);
      await countersignSign(nip, publicationId, csSignTarget.id, csSignFile);
      toast.success(t('documents.publications.countersigns.signSuccess'));
      setCsSignTarget(null);
      setCsSignFile(null);
      await loadDetail();
      onChanged();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('documents.publications.countersigns.signError'),
      );
      console.error(error);
    } finally {
      setCsSigning(false);
    }
  };

  const handleCountersignDownload = async (row: DocumentCountersignRow) => {
    if (!publicationId) return;
    setCsDownloadingId(row.id);
    try {
      const base = detail ? sanitizeFileNamePart(detail.number) : publicationId;
      await countersignFileDownload(
        nip,
        publicationId,
        row.id,
        `${base}-kontrasygnata-${sanitizeFileNamePart(row.fullName)}`,
      );
    } catch {
      /* session expired — handled by the global modal */
    } finally {
      setCsDownloadingId(null);
    }
  };

  const countersigns = detail?.countersigns ?? [];
  // "Fully signed": the employee's acceptance reached its final state AND
  // every representative countersign is signed.
  const fullySigned =
    countersigns.length > 0 &&
    countersigns.every((c) => c.status === 'signed') &&
    (detail?.acceptances ?? []).every((a) => a.status === 'accepted' || a.status === 'signed');

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
        <SheetContent className="flex w-full flex-col gap-0 sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {detail ? `${detail.number} — ${detail.title}` : t('documents.publications.detailTitle')}
            </SheetTitle>
            {detail && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <Badge variant="outline">{t(`documents.publications.kinds.${detail.kind}`)}</Badge>
                <Badge variant="outline">
                  {detail.kind === 'individual'
                    ? (detail.employeeName ?? '—')
                    : detail.audience
                      ? t(`documents.templates.audiences.${detail.audience}`)
                      : '—'}
                </Badge>
                <Badge variant="outline">
                  {t(`documents.templates.acceptances.${detail.acceptanceMode}`)}
                </Badge>
                {detail.deadline && (
                  <span className="text-muted-foreground text-xs">
                    {t('documents.publications.deadline')}: {detail.deadline}
                  </span>
                )}
              </div>
            )}
          </SheetHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
            <Button variant="outline" size="sm" disabled={downloading || !detail} onClick={handleDownload}>
              {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              {t('documents.publications.download')}
            </Button>

            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : detail && detail.acceptances.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="text-muted-foreground">{t('documents.publications.employeeCol')}</TableHead>
                      <TableHead className="text-muted-foreground w-28">{t('documents.publications.status')}</TableHead>
                      <TableHead className="text-muted-foreground w-28">{t('documents.publications.method')}</TableHead>
                      <TableHead className="text-muted-foreground w-28">{t('documents.publications.acceptedAt')}</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.acceptances.map((row) => {
                      // A signature panel for every row that holds a file — a
                      // returned/signed scan, or (2026-09-05) the digitally
                      // signed copy that came back for an ELECTRONIC acceptance.
                      const showSignature =
                        row.status === 'returned' || row.status === 'signed' || !!row.hasSignedFile;
                      return (
                      <Fragment key={row.id}>
                      <TableRow className={showSignature ? 'border-b-0' : undefined}>
                        <TableCell className="py-2">{row.employeeName}</TableCell>
                        <TableCell className="py-2">
                          <Badge variant="outline" className={STATUS_CHIP[row.status]}>
                            {t(`documents.publications.statuses.${row.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2">
                          {row.method ? t(`documents.publications.methods.${row.method}`) : '—'}
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          {(row.acceptedAt ?? row.returnedAt) ? new Date((row.acceptedAt ?? row.returnedAt)!).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell className="py-2">
                            <div className="flex items-center justify-end gap-1">
                              {detail.acceptanceMode === 'physical' && row.status === 'pending' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8"
                                  title={t('documents.publications.markSigned')}
                                  onClick={() => { setSignFile(null); setSignTarget(row); }}
                                >
                                  <UploadCloud className="size-4" />
                                </Button>
                              )}
                              {(row.status === 'returned' || row.status === 'signed' || row.hasSignedFile) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8"
                                  aria-label={t('documents.publications.downloadReturned')}
                                  title={t('documents.publications.downloadReturned')}
                                  disabled={downloadingRowId === row.id}
                                  onClick={() => handleDownloadReturned(row)}
                                >
                                  {downloadingRowId === row.id ? (
                                    <Loader2 className="size-4 animate-spin" />
                                  ) : (
                                    <Download className="size-4" />
                                  )}
                                </Button>
                              )}
                              {(row.status === 'signed' || row.status === 'accepted') && row.hasSignedFile && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8"
                                  aria-label={t('documents.publications.attachRevision')}
                                  title={t('documents.publications.attachRevision')}
                                  onClick={() => { setRevisionFile(null); setRevisionPreview(null); setRevisionTarget(row); }}
                                >
                                  <FileUp className="size-4" />
                                </Button>
                              )}
                              {row.status === 'returned' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="size-8 text-emerald-600 dark:text-emerald-400"
                                  title={t('documents.publications.confirmReturn')}
                                  disabled={confirmingId === row.id}
                                  onClick={() => handleConfirmReturn(row)}
                                >
                                  {confirmingId === row.id ? (
                                    <Loader2 className="size-4 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="size-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                      </TableRow>
                      {showSignature && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={5} className="pt-0 pb-2">
                            {verifyingRows[row.id] ? (
                              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                                <Loader2 className="size-3.5 animate-spin" />
                                {t('signatures.verifying')}
                              </span>
                            ) : row.signatureVerification != null ? (
                              <div className="flex flex-col items-start gap-1">
                                <SignatureDetails verification={row.signatureVerification} />
                                {/* A stored verdict can predate a verifier fix — and
                                    re-running it is also what lets a scan that was
                                    already signed settle the acceptance/countersigns. */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-muted-foreground h-6 gap-1 px-1.5 text-xs"
                                  onClick={() => runVerify(row)}
                                >
                                  <RotateCw className="size-3.5" />
                                  {t('signatures.retry')}
                                </Button>
                              </div>
                            ) : verifyErrorRows[row.id] ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground h-6 gap-1 px-1.5 text-xs"
                                onClick={() => runVerify(row)}
                              >
                                <RotateCw className="size-3.5" />
                                {t('signatures.retry')}
                              </Button>
                            ) : (
                              <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                                <Loader2 className="size-3.5 animate-spin" />
                                {t('signatures.verifying')}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                      </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">{t('documents.publications.noAcceptances')}</p>
            )}

            {!loading && countersigns.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    {t('documents.publications.countersigns.title')}
                  </p>
                  {fullySigned && (
                    <Badge
                      variant="outline"
                      className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                    >
                      <CheckCheck className="size-3.5" />
                      {t('documents.publications.countersigns.fullySigned')}
                    </Badge>
                  )}
                </div>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableBody>
                      {countersigns.map((row) => {
                        // A countersign recognised straight from the returned
                        // scan carries a verification result but no file of its
                        // own — its signature block must still be shown.
                        const showSignature =
                          row.status === 'signed' && (row.hasFile || row.signatureVerification != null);
                        return (
                          <Fragment key={row.id}>
                            <TableRow className={showSignature ? 'border-b-0' : undefined}>
                              <TableCell className="py-2">
                                <span className="block truncate">{row.fullName}</span>
                                <span className="text-muted-foreground block text-xs">{row.title}</span>
                              </TableCell>
                              <TableCell className="w-36 py-2">
                                <Badge variant="outline" className={COUNTERSIGN_CHIP[row.status]}>
                                  {t(`documents.publications.countersigns.statuses.${row.status}`)}
                                </Badge>
                              </TableCell>
                              <TableCell className="w-28 py-2 text-sm">
                                {row.signedAt ? new Date(row.signedAt).toLocaleDateString() : '—'}
                              </TableCell>
                              <TableCell className="w-10 py-2">
                                <div className="flex items-center justify-end gap-1">
                                  {row.status === 'pending' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="size-8"
                                      title={t('documents.publications.countersigns.sign')}
                                      onClick={() => { setCsSignFile(null); setCsSignTarget(row); }}
                                    >
                                      <PenLine className="size-4" />
                                    </Button>
                                  )}
                                  {row.hasFile && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="size-8"
                                      aria-label={t('documents.publications.countersigns.downloadFile')}
                                      title={t('documents.publications.countersigns.downloadFile')}
                                      disabled={csDownloadingId === row.id}
                                      onClick={() => handleCountersignDownload(row)}
                                    >
                                      {csDownloadingId === row.id ? (
                                        <Loader2 className="size-4 animate-spin" />
                                      ) : (
                                        <Download className="size-4" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                            {showSignature && (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={4} className="pt-0 pb-2">
                                  {csVerifyingRows[row.id] ? (
                                    <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                                      <Loader2 className="size-3.5 animate-spin" />
                                      {t('signatures.verifying')}
                                    </span>
                                  ) : row.signatureVerification != null ? (
                                    <SignatureDetails verification={row.signatureVerification} />
                                  ) : csVerifyErrorRows[row.id] ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-muted-foreground h-6 gap-1 px-1.5 text-xs"
                                      onClick={() => runCsVerify(row)}
                                    >
                                      <RotateCw className="size-3.5" />
                                      {t('signatures.retry')}
                                    </Button>
                                  ) : (
                                    <span className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                                      <Loader2 className="size-3.5 animate-spin" />
                                      {t('signatures.verifying')}
                                    </span>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={signTarget !== null}
        onOpenChange={(o) => { if (!o && !signing) { setSignTarget(null); setSignFile(null); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.publications.markSignedTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pub-signed-file" className="text-muted-foreground text-xs">
              {t('documents.publications.markSignedFile')}
            </label>
            <input
              id="pub-signed-file"
              type="file"
              accept="application/pdf,image/*"
              className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => setSignFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={signing}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={!signFile || signing} onClick={handleMarkSigned}>
              {signing && <Loader2 className="size-4 animate-spin" />}
              {t('documents.publications.markSigned')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={revisionTarget !== null}
        onOpenChange={(o) => {
          if (!o && !attachingRevision && !previewingRevision) {
            setRevisionTarget(null); setRevisionFile(null); setRevisionPreview(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.publications.attachRevisionTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="pub-revision-file" className="text-muted-foreground text-xs">
              {t('documents.publications.attachRevisionFile')}
            </label>
            <input
              id="pub-revision-file"
              type="file"
              accept="application/pdf"
              className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => { setRevisionFile(e.target.files?.[0] ?? null); setRevisionPreview(null); }}
            />
            <p className="text-muted-foreground text-xs">
              {t('documents.publications.attachRevisionHint')}
            </p>
            {revisionPreview && (
              <div className="mt-1 flex flex-col gap-1.5 rounded-md border p-2.5">
                <p className="text-xs font-medium">{t('documents.publications.attachRevisionAdds')}</p>
                <SignatureDetails verification={{ verifiedAt: '', signatures: revisionPreview.addedSignatures }} />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={attachingRevision || previewingRevision}>{t('common.cancel')}</AlertDialogCancel>
            {revisionPreview === null ? (
              // A plain Button: the dialog must stay open to show the verdict.
              <Button disabled={!revisionFile || previewingRevision} onClick={handlePreviewRevision}>
                {previewingRevision && <Loader2 className="size-4 animate-spin" />}
                {t('documents.publications.attachRevisionCheck')}
              </Button>
            ) : (
              <AlertDialogAction disabled={!revisionFile || attachingRevision} onClick={handleAttachRevision}>
                {attachingRevision && <Loader2 className="size-4 animate-spin" />}
                {t('documents.publications.attachRevisionApply')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Representative countersign: the file is OPTIONAL — a digitally
          signed PDF / scan, or nothing when signed in person on paper. */}
      <AlertDialog
        open={csSignTarget !== null}
        onOpenChange={(o) => { if (!o && !csSigning) { setCsSignTarget(null); setCsSignFile(null); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('documents.publications.countersigns.signTitle', {
                name: csSignTarget?.fullName ?? '',
              })}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="cs-signed-file" className="text-muted-foreground text-xs">
              {t('documents.publications.countersigns.signFile')}
            </label>
            <input
              id="cs-signed-file"
              type="file"
              accept="application/pdf,image/*"
              className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
              onChange={(e) => setCsSignFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-muted-foreground text-xs">
              {t('documents.publications.countersigns.signFileHint')}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={csSigning}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={csSigning} onClick={handleCountersignSign}>
              {csSigning && <Loader2 className="size-4 animate-spin" />}
              {t('documents.publications.countersigns.sign')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
